/**
 * Permission utilities for Organizations & Roles
 *
 * These functions determine user permissions based on their role and organization data.
 * Extracted to enable unit testing without Firebase dependencies.
 */

/**
 * Check if user has the owner role
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const isUserOwner = (userData) => {
  return userData?.rol === 'owner';
};

/**
 * Check if user has the operator role
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const isUserOperator = (userData) => {
  return userData?.rol === 'operator';
};

/**
 * Check if user has the client role
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const isUserClient = (userData) => {
  return userData?.rol === 'client';
};

/**
 * Check if user is a broker (owner or operator)
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const isBrokerRole = (userData) => {
  return isUserOwner(userData) || isUserOperator(userData);
};

/**
 * Check if user can invite client users (org owners only)
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const canInviteClientUsers = (userData) => {
  if (!userData) return false;
  return hasOrganization(userData) && isUserOwner(userData);
};

/**
 * Check if user belongs to an organization
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const hasOrganization = (userData) => {
  return Boolean(userData?.org);
};

/**
 * Check if user has dependientes (legacy hierarchical model)
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const hasDependientes = (userData) => {
  return Boolean(
    userData?.dependientes &&
    Array.isArray(userData.dependientes) &&
    userData.dependientes.length > 0
  );
};

/**
 * Check if user can access Reportes
 * - Org owners can access
 * - Legacy users with dependientes can access
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const canAccessReportes = (userData) => {
  if (!userData || isUserClient(userData)) return false;

  // Org-based: only owners
  if (hasOrganization(userData)) {
    return isUserOwner(userData);
  }

  // Legacy: users with dependientes (bosses)
  return hasDependientes(userData);
};

/**
 * Check if user can access Tarifas (commissions tables)
 * - Only org owners can access
 * - Legacy users always have access (no org restriction)
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const canAccessTarifas = (userData) => {
  if (!userData || isUserClient(userData)) return false;

  // Org-based: only owners
  if (hasOrganization(userData)) {
    return isUserOwner(userData);
  }

  // Legacy: all users have access
  return true;
};

/**
 * Check if user can create new users
 * - Org owners can create users in their org
 * - Legacy users with dependientes array can create
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const canCreateUsers = (userData) => {
  if (!userData || isUserClient(userData)) return false;

  // Org-based: only owners
  if (hasOrganization(userData)) {
    return isUserOwner(userData);
  }

  // Legacy: users who have or can have dependientes
  return hasDependientes(userData);
};

/**
 * Check if user can manage team (view/edit team members)
 * - Org owners can manage team
 * - Legacy users with dependientes can manage
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const canManageTeam = (userData) => {
  if (!userData || isUserClient(userData)) return false;

  // Org-based: only owners
  if (hasOrganization(userData)) {
    return isUserOwner(userData);
  }

  // Legacy: users with dependientes
  return hasDependientes(userData);
};

/**
 * Check if user can edit organization settings (name, etc.)
 * - Only org owners can edit org settings
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const canEditOrganization = (userData) => {
  if (!userData || isUserClient(userData)) return false;
  return hasOrganization(userData) && isUserOwner(userData);
};

/**
 * Check if user can access Convenios (brokers only)
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const canAccessConvenios = (userData) => {
  if (!userData) return false;
  return isBrokerRole(userData);
};

/**
 * Check if user can invite convenio users (org owners only)
 * @param {Object} userData - User document data
 * @returns {boolean}
 */
export const canInviteConvenioUsers = (userData) => {
  if (!userData) return false;
  return hasOrganization(userData) && isUserOwner(userData);
};

/**
 * Check if a client user has access to a specific convenio
 * @param {Object} userData - User document data
 * @param {string} convenioId - Convenio ID to check
 * @returns {boolean}
 */
export const hasConvenioAccess = (userData, convenioId) => {
  if (!userData?.convenioAccess) return false;
  return convenioId in userData.convenioAccess;
};

/**
 * Check if a client user has access to a specific project in a convenio
 * @param {Object} userData - User document data
 * @param {string} convenioId - Convenio ID
 * @param {string} proyectoId - Project ID (can be null for unassigned contracts)
 * @returns {boolean}
 */
export const hasConvenioProjectAccess = (userData, convenioId, proyectoId) => {
  if (!userData?.convenioAccess) return false;
  const access = userData.convenioAccess[convenioId];
  if (!access) return false;
  if (access.fullAccess) return true;
  if (!proyectoId) return false;
  return Array.isArray(access.proyectoIds) && access.proyectoIds.includes(proyectoId);
};

/**
 * Get user's permission summary
 * @param {Object} userData - User document data
 * @returns {Object} Permission flags
 */
export const getPermissionSummary = (userData) => {
  return {
    isOwner: isUserOwner(userData),
    isOperator: isUserOperator(userData),
    isClient: isUserClient(userData),
    isBroker: isBrokerRole(userData),
    hasOrg: hasOrganization(userData),
    canAccessReportes: canAccessReportes(userData),
    canAccessTarifas: canAccessTarifas(userData),
    canCreateUsers: canCreateUsers(userData),
    canManageTeam: canManageTeam(userData),
    canEditOrganization: canEditOrganization(userData),
    canInviteClientUsers: canInviteClientUsers(userData),
  };
};
